import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { useAppDispatch, useAppSelector } from '../../../hooks/useRedux';
import { fetchBankIdVerificationStatus, LatestDocumentStatus } from '../../../features/bankid/bankid.slice';
import BankIDAuth from '../../bank-id-auth/bank-id-auth';
import './onboarding-identity-verification.style.scss';

/**
 * Onboarding identity-verification step. Reuses the invoice tool's
 * <BankIDAuth> modal shell and offers the SAME two paths the invoice flow does:
 *   • Verify with BankID (same-device / QR — handled inside BankIDComponent)
 *   • Upload documents (ID document + address proof) — POSTed to the same
 *     `/bankid-verification/upload-documents` endpoint the invoice flow uses.
 *
 * Behaviour matches the invoice tool (create-invoice), only the UI differs:
 *   • BankID name/SSN mismatch → "request to admin" flow is inherited for free,
 *     since it lives inside the shared <BankIDComponent> (driven by redux flags
 *     the `checkBankIdVerificationAuthStatus` poll sets); we render that same
 *     component with `collectMode="verification"`.
 *   • Document upload → shows a PENDING state ("request sent to admin, approval
 *     within 24 hours") driven by `latestDocumentStatus === Pending`, exactly
 *     like create-invoice — NOT an instant "verified". create-invoice's own
 *     components/logic are untouched; this only adds an onboarding-styled UI.
 *
 * `onSuccess` fires only when identity is actually verified/approved.
 * `onPending` fires when documents are submitted and awaiting admin approval.
 * `onClose` bubbles the close so the wizard can enforce its required gating.
 */
type Mode = 'select' | 'bankid' | 'document';

interface OnboardingIdentityVerificationProps {
  onSuccess: () => void;
  onClose: () => void;
  /** Documents submitted → awaiting admin approval (not yet verified). */
  onPending?: () => void;
  /** Stepper header shown inside the BankID modal so the flow stays unified. */
  stepper?: React.ReactNode;
}

const MAX_FILE_BYTES = 5 * 1024 * 1024; // 5 MB
const ACCEPT = '.pdf,.png,.jpg,.jpeg';

const OnboardingIdentityVerification: React.FC<OnboardingIdentityVerificationProps> = ({
  onSuccess,
  onClose,
  onPending,
  stepper,
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const canVerifyViaBankId = useAppSelector((s: any) => s?.bankidAuthFlow?.canVerifyViaBankId);
  const latestDocumentStatus = useAppSelector((s: any) => s?.bankidAuthFlow?.latestDocumentStatus);
  const isIdentityVerified = useAppSelector((s: any) => s?.bankidAuthFlow?.isIdentityVerified);
  // Per-document decisions from admin: [0] = ID document, [1] = address proof.
  const documents = useAppSelector((s: any) => s?.bankidAuthFlow?.documents);
  const idDoc = documents?.[0];
  const addressDoc = documents?.[1];
  const idDocApproved = idDoc?.status === 1;
  const addressDocApproved = addressDoc?.status === 1;
  const idDocRejected = idDoc?.status === 2;
  const addressDocRejected = addressDoc?.status === 2;
  const hasRejection = idDocRejected || addressDocRejected;

  // If BankID isn't an available method, jump straight to the document path.
  const [mode, setMode] = useState<Mode>(canVerifyViaBankId === false ? 'document' : 'select');
  // Locally remember a just-submitted upload so the pending screen shows even
  // before the re-fetched status resolves.
  const [justSubmitted, setJustSubmitted] = useState(false);

  const isPending =
    justSubmitted || latestDocumentStatus === LatestDocumentStatus.Pending;

  // Resolve current status on mount, then react to it: approved → verified,
  // pending → awaiting admin (same gate the invoice tool uses).
  useEffect(() => {
    dispatch(fetchBankIdVerificationStatus() as any);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  useEffect(() => {
    if (isIdentityVerified === true || latestDocumentStatus === LatestDocumentStatus.Approved) {
      onSuccess();
    } else if (latestDocumentStatus === LatestDocumentStatus.Pending) {
      onPending?.();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isIdentityVerified, latestDocumentStatus]);

  // A document was rejected → drop the user straight into the document screen so
  // they see the reason and can re-upload (skip the method-selection screen).
  useEffect(() => {
    if (hasRejection) setMode('document');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasRejection]);

  const [idFile, setIdFile] = useState<File | null>(null);
  const [idType, setIdType] = useState<'passport' | 'national_id' | ''>('');
  const [addrFile, setAddrFile] = useState<File | null>(null);
  const [addrName, setAddrName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const idInputRef = useRef<HTMLInputElement>(null);
  const addrInputRef = useRef<HTMLInputElement>(null);

  const pickFile = (
    e: React.ChangeEvent<HTMLInputElement>,
    setter: (f: File | null) => void,
  ) => {
    const file = e.target.files?.[0] || null;
    setError(null);
    if (file && file.size > MAX_FILE_BYTES) {
      setError(t('FILE_TOO_LARGE') || 'File is too large (max 5 MB)');
      return;
    }
    setter(file);
  };

  // A slot is "satisfied" if the admin already approved it OR the user just
  // picked a new file for it (matching the invoice tool).
  const idDocSatisfied = idDocApproved || (!!idFile && !!idType);
  const addressDocSatisfied = addressDocApproved || (!!addrFile && !!addrName.trim());
  const docSubmitDisabled = !idDocSatisfied || !addressDocSatisfied || submitting;

  const handleDocumentSubmit = async () => {
    if (docSubmitDisabled) return;
    try {
      setSubmitting(true);
      setError(null);
      const fd = new FormData();
      // Only (re)upload the slots that aren't already approved.
      if (!idDocApproved && idFile) {
        fd.append('files', idFile, idFile.name);
        fd.append('documentNames', t(`ID_DOCUMENT_TYPE_${idType.toUpperCase()}`));
      }
      if (!addressDocApproved && addrFile) {
        fd.append('files', addrFile, addrFile.name);
        fd.append('documentNames', addrName.trim());
      }

      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const res = await fetch(
        `${import.meta.env.VITE_API_URL}/bankid-verification/upload-documents`,
        {
          method: 'POST',
          headers: { Authorization: `Bearer ${user?.token}` },
          body: fd,
        },
      );
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data?.message || 'Failed to upload documents');
      }
      // Same as create-invoice: submitting documents does NOT verify instantly —
      // it goes to "pending admin approval". Re-fetch status; the pending screen
      // shows via `latestDocumentStatus === Pending`.
      setJustSubmitted(true);
      dispatch(fetchBankIdVerificationStatus() as any);
      toast.success(t('DOCUMENTS_SUBMITTED_SUCCESSFULLY') || 'Documents submitted for review');
      onPending?.();
    } catch (err: any) {
      const msg = err?.message || 'Failed to upload documents';
      setError(msg);
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const backButton = (
    <button
      type="button"
      className="idv-back-btn"
      onClick={() => {
        setMode('select');
        setError(null);
      }}
      aria-label={t('BACK') || 'Back'}
    >
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
        <path d="M19 12H5M5 12L12 19M5 12L12 5" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </button>
  );

  const selectionScreen = (
    <div className="idv-select">
      <p className="idv-select-title">{t('CHOOSE_VERIFICATION_METHOD')}</p>
      {/* Onboarding-specific wording: the shared ID_VERIFY_METHOD_INTRO talks
          about creating or drafting an invoice, which is true where the invoice
          tool raises this screen but nonsense here — nothing is being invoiced
          during setup. create-invoice.page.tsx keeps the original key. */}
      <p className="idv-select-desc">{t('ID_VERIFY_METHOD_INTRO_ONBOARDING')}</p>
      <div className={`idv-tabs${canVerifyViaBankId === false ? ' idv-tabs--single' : ''}`} role="tablist">
        {canVerifyViaBankId !== false && (
          <button type="button" role="tab" className="idv-tab" onClick={() => setMode('bankid')}>
            <div className="option-icon">
              <img src="/assets/svg/bankid-vector-logo-color.svg" alt="BankID" width="28" height="28" />
            </div>
            <div className="idv-tab-text">
              <h3>{t('VERIFY_WITH_BANKID')}</h3>
              <span>{t('VERIFY_WITH_BANKID_SUBTITLE')}</span>
            </div>
          </button>
        )}
        <button type="button" role="tab" className="idv-tab" onClick={() => setMode('document')}>
          <div className="option-icon">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
              <path d="M14 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V20C4 20.5304 4.21071 21.0391 4.58579 21.4142C4.96086 21.7893 5.46957 22 6 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V8L14 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              <polyline points="14,2 14,8 20,8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              <line x1="12" y1="11" x2="12" y2="17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
              <polyline points="9,14 12,11 15,14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>
          <div className="idv-tab-text">
            <h3>{t('UPLOAD_ID_DOCUMENT')}</h3>
            <span>PDF, PNG, JPG · max 5 MB</span>
          </div>
        </button>
      </div>
    </div>
  );

  const pendingScreen = (
    <div className="idv-pending">
      <div className="idv-pending-badge" aria-hidden="true">
        <svg width="34" height="34" viewBox="0 0 24 24" fill="none">
          <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.7" />
          <path d="M12 7.5V12l3 2" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </div>
      <h3 className="idv-pending-title">{t('ID_DOCUMENT_PENDING_TITLE')}</h3>
      <p className="idv-pending-msg">{t('ID_DOCUMENT_PENDING_MSG')}</p>
      <p className="idv-pending-24h">
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.7" />
          <path d="M12 8v4l2.5 1.5" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
        {t('ID_DOCUMENT_PENDING_24H')}
      </p>
    </div>
  );

  const RejectBanner: React.FC<{ label: string; reason: string }> = ({ label, reason }) => (
    <div className="idv-reject" role="alert">
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path d="M12 9v4m0 4h.01M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0Z"
          stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
      <div>
        <p className="idv-reject-label">{label}</p>
        <p className="idv-reject-reason">{reason}</p>
      </div>
    </div>
  );

  const ApprovedChip: React.FC<{ label: string; name?: string }> = ({ label, name }) => (
    <div className="idv-approved">
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <circle cx="12" cy="12" r="10" fill="currentColor" opacity="0.15" />
        <path d="M7.5 12.5l3 3 6-6.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
      <div>
        <p className="idv-approved-label">{label}</p>
        {name && <span className="idv-approved-name">{name}</span>}
      </div>
    </div>
  );

  const documentScreen = (
    <div className="idv-doc-form">
      {/* ── ID document ── */}
      {idDocApproved ? (
        <ApprovedChip label={`${t('UPLOAD_ID_DOCUMENT')} — ${t('ID_DOCUMENT_APPROVED_LABEL')}`} name={idDoc?.documentName} />
      ) : (
        <>
          <div className="idv-doc-block">
            <label className="idv-doc-label">
              {t('ID_DOCUMENT_TYPE_LABEL')} <span className="idv-req">*</span>
            </label>
            <div className="idv-type-options">
              {([
                { value: 'passport', label: t('ID_DOCUMENT_TYPE_PASSPORT'), icon: '🛂' },
                { value: 'national_id', label: t('ID_DOCUMENT_TYPE_NATIONAL_ID'), icon: '🪪' },
              ] as const).map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  className={`idv-type-option${idType === opt.value ? ' is-selected' : ''}`}
                  onClick={() => setIdType(opt.value)}
                >
                  <span className="idv-type-icon">{opt.icon}</span>
                  <span className="idv-type-name">{opt.label}</span>
                  <span className="idv-type-radio" />
                </button>
              ))}
            </div>
          </div>

          {idDocRejected && idDoc?.rejectionReason && (
            <RejectBanner label={t('ID_DOCUMENT_REJECTED_LABEL')} reason={idDoc.rejectionReason} />
          )}

          <input ref={idInputRef} type="file" accept={ACCEPT} style={{ display: 'none' }} onChange={(e) => pickFile(e, setIdFile)} />
          <button type="button" className={`idv-upload${idFile ? ' has-file' : ''}`} onClick={() => idInputRef.current?.click()}>
            <span className="idv-upload-icon">📄</span>
            <span className="idv-upload-text">
              {idFile ? idFile.name : idDocRejected ? t('ID_DOCUMENT_REUPLOAD_LABEL') : t('UPLOAD_ID_DOCUMENT')}
            </span>
          </button>
        </>
      )}

      {/* ── Address proof ── */}
      <div className="idv-doc-block">
        <label className="idv-doc-label">
          {t('ADDRESS_PROOF') || 'Proof of address'} <span className="idv-req">*</span>
        </label>

        {addressDocApproved ? (
          <ApprovedChip label={t('ADDRESS_PROOF_APPROVED_LABEL')} name={addressDoc?.documentName} />
        ) : (
          <>
            {addressDocRejected && addressDoc?.rejectionReason && (
              <RejectBanner label={t('ADDRESS_PROOF_REJECTED_LABEL')} reason={addressDoc.rejectionReason} />
            )}
            <input ref={addrInputRef} type="file" accept={ACCEPT} style={{ display: 'none' }} onChange={(e) => pickFile(e, setAddrFile)} />
            <button type="button" className={`idv-upload${addrFile ? ' has-file' : ''}`} onClick={() => addrInputRef.current?.click()}>
              <span className="idv-upload-icon">🏠</span>
              <span className="idv-upload-text">
                {addrFile ? addrFile.name : addressDocRejected ? t('ADDRESS_PROOF_REUPLOAD_LABEL') : (t('UPLOAD_ADDRESS_PROOF') || 'Upload proof of address')}
              </span>
            </button>
            <input
              type="text"
              className="idv-text-input"
              placeholder={t('DOCUMENT_NAME') || 'Document name'}
              value={addrName}
              onChange={(e) => setAddrName(e.target.value)}
            />
          </>
        )}
      </div>

      {error && <p className="idv-error">⚠ {error}</p>}

      <button type="button" className="idv-submit" disabled={docSubmitDisabled} onClick={handleDocumentSubmit}>
        {submitting ? (t('SUBMITTING') || 'Submitting…') : (t('SUBMIT') || 'Submit')}
      </button>
    </div>
  );

  return (
    <BankIDAuth
      mode="login"
      collectMode="verification"
      className="login-theme onboarding-idv"
      title={t('ID_VERIFICATION')}
      showCloseButton={false}
      hideOptions={mode !== 'bankid' || isPending}
      autoInitialize={mode === 'bankid' && !isPending}
      onSuccess={onSuccess}
      onClose={onClose}
      subHeaderContent={
        (stepper || (mode !== 'select' && !isPending)) ? (
          <>
            {stepper}
            {mode !== 'select' && !isPending && backButton}
          </>
        ) : undefined
      }
      footerContent={
        isPending
          ? pendingScreen
          : mode === 'select'
          ? selectionScreen
          : mode === 'document'
          ? documentScreen
          : undefined
      }
    />
  );
};

export default OnboardingIdentityVerification;
